import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, BRAND } from "../lib/api";
import { Check, Calendar, Phone, Home, MapPin, Clock } from "lucide-react";

export default function Confirmation() {
  const { rn } = useParams();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [paid, setPaid] = useState(null);

  const { data: r } = useQuery({
    queryKey: ["reservation", rn],
    queryFn: async () => (await api.get(`/reservations/${rn}`)).data,
  });

  useEffect(() => {
    if (!sessionId) return;
    let stopped = false, tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setPaid(true); return; }
      } catch (e) { console.error("payment status poll error", e); }
      if (!stopped && tries < 15) { tries++; setTimeout(poll, 2000); }
      else setPaid(false);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  const icsUrl = r ? (() => {
    const dtStart = (r.pickup_date && r.pickup_time) ? `${r.pickup_date.replace(/-/g,'')}T${r.pickup_time.replace(':','')}00` : '';
    const body = ["BEGIN:VCALENDAR","VERSION:2.0","BEGIN:VEVENT",
      `UID:${r.reservation_number}@lume`,
      `SUMMARY:Lume Enterprise Ride ${r.reservation_number}`,
      `DTSTART:${dtStart}`,
      `LOCATION:${r.pickup_location}`,
      `DESCRIPTION:${r.vehicle_class_name} - ${r.destination || 'As directed'}`,
      "END:VEVENT","END:VCALENDAR"].join("\r\n");
    return `data:text/calendar;charset=utf-8,${encodeURIComponent(body)}`;
  })() : "#";

  if (!r) return <div className="section-container py-24 text-slate-400">Loading reservation…</div>;

  return (
    <div className="section-container py-16 sm:py-24 max-w-3xl">
      <div className="lume-card p-8 sm:p-12 text-center fade-up" data-testid="confirmation-card">
        <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid var(--lume-border-gold)"}}>
          <Check className="text-[color:var(--lume-gold)]" size={26}/>
        </div>
        <div className="eyebrow mt-6 mb-3">Reservation Confirmed</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-white">Thank you for choosing Lume Enterprise.</h1>
        <div className="mt-6 inline-block px-5 py-2 rounded-full border border-[color:var(--lume-gold)]/40 font-mono text-sm text-[color:var(--lume-gold-light)]" data-testid="confirmation-rn">
          Reservation #{r.reservation_number}
        </div>
        {sessionId && (
          <div className="mt-4 text-sm">
            {paid === true && <span className="text-emerald-400" data-testid="payment-status">Payment method secured ✓</span>}
            {paid === false && <span className="text-amber-400">Payment pending confirmation — we'll follow up shortly.</span>}
            {paid === null && <span className="text-slate-400">Verifying payment…</span>}
          </div>
        )}
      </div>

      <div className="lume-card p-8 mt-6" data-testid="confirmation-details">
        <div className="eyebrow mb-4">Trip Summary</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Row k="Customer" v={r.customer_name}/>
          <Row k="Service" v={r.service_type.replace("-", " ").replace(/\b\w/g, l=>l.toUpperCase())}/>
          <Row k="Pickup" v={r.pickup_location} icon={MapPin}/>
          <Row k="Destination" v={r.destination || "As directed"} icon={MapPin}/>
          <Row k="Date" v={r.pickup_date} icon={Calendar}/>
          <Row k="Time" v={r.pickup_time} icon={Clock}/>
          <Row k="Vehicle Class" v={r.vehicle_class_name}/>
          <Row k="Passengers" v={r.passengers}/>
          <Row k="Status" v={<span className={`status-pill status-${r.status}`}>{r.status.replace("_"," ")}</span>}/>
        </div>
      </div>

      <div className="mt-6 lume-card p-6 text-sm text-slate-300">
        A confirmation email has been sent to <strong className="text-white">{r.customer_email}</strong>. Our concierge team may reach out to confirm final details.
      </div>

      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <a href={icsUrl} download={`Lume-${r.reservation_number}.ics`} className="btn-outline-gold" data-testid="ical-btn"><Calendar size={14} className="inline mr-2"/>Add to Calendar</a>
        <a href={`tel:${BRAND.phoneRaw}`} className="btn-outline-gold" data-testid="call-btn"><Phone size={14} className="inline mr-2"/>Contact Lume</a>
        <Link to="/" className="btn-ghost" data-testid="home-btn"><Home size={14} className="inline mr-2"/>Return Home</Link>
      </div>
    </div>
  );
}

function Row({ k, v, icon: Icon }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && <Icon className="text-[color:var(--lume-gold)] mt-0.5" size={14}/>}
      <div className="flex-1 flex items-baseline justify-between gap-3">
        <div className="text-slate-400 text-xs uppercase tracking-widest">{k}</div>
        <div className="text-white text-right">{v}</div>
      </div>
    </div>
  );
}
